import { all, takeLatest, call, put } from "redux-saga/effects";
import { Actions as AuthActions, setAuthStatus } from "./actions";
import * as localStorage from "@utility/storage/local";
import * as sessionStorage from "@utility/storage/session";
import { getUserProfile, logoutRequest } from "@src/api/lib/auth";
import { refreshSession, broadcastLogout, invalidateAccessToken } from "@src/api/config";
import { getAccessToken, getRefreshToken, lockAuthStorage, isAuthStorageLocked } from "@utility/storage/auth";
import { withSessionLock, takeCompanyChangedNotice } from "@utility/storage/browserSession";
import { fetchResources } from "../resource/actions";
import { fetchCompanySettings } from "../companySettings/actions";
import { appLoading } from "../app/actions";

function* onLogout() {
  yield call(lockAuthStorage);
  const refreshToken = getRefreshToken();
  yield call(broadcastLogout);
  try {
    yield call(withSessionLock, () => logoutRequest(refreshToken));
  } catch (error) {
    // Still clear local session if the API call fails
  }
  yield call(localStorage.clear);
  yield call(lockAuthStorage);
  yield call(sessionStorage.clear);
}

function* markSignedOut() {
  yield call(invalidateAccessToken);
  yield put(
    setAuthStatus({
      authToken: null,
      userProfile: null,
      status: "UNAUTHENTICATED",
    })
  );
  yield put(appLoading(false));
}

function* markStartupError() {
  yield put(
    setAuthStatus({
      authToken: null,
      userProfile: null,
      status: "ERROR",
    })
  );
  yield put(appLoading(false));
}

function* establishSession(jwt) {
  const myProfile = yield call(getUserProfile, jwt);
  yield put(
    setAuthStatus({
      authToken: jwt,
      userProfile: myProfile.data,
      status: "AUTHENTICATED",
    })
  );
  yield put(fetchResources());
  yield put(fetchCompanySettings());
}

function AxiosCancel(error) {
  return (
    error?.message === "session-changed" ||
    error?.message === "company-switch" ||
    error?.__CANCEL__
  );
}

function* checkAuthStatus() {
  if (isAuthStorageLocked()) {
    yield* markSignedOut();
    return;
  }

  let jwt = getAccessToken();

  if (!jwt) {
    try {
      jwt = yield call(refreshSession);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || error?.message === "LOGGED_OUT") {
        yield* markSignedOut();
        return;
      }
      yield* markStartupError();
      return;
    }
  }

  if (!jwt) {
    takeCompanyChangedNotice();
    yield* markSignedOut();
    return;
  }

  try {
    yield* establishSession(jwt);
  } catch (error) {
    const status = error?.response?.status;
    const code = error?.response?.data?.code;
    if (AxiosCancel(error)) return;

    if (status === 401 && code === "SESSION_SUPERSEDED") {
      yield put(
        setAuthStatus({
          authToken: null,
          userProfile: null,
          status: "UNKNOWN",
        })
      );
      yield put(appLoading(true));
      return;
    }

    if (status === 401) {
      yield call(invalidateAccessToken);
      try {
        const refreshed = yield call(refreshSession, true);
        if (refreshed) {
          yield* establishSession(refreshed);
          return;
        }
      } catch (refreshError) {
        const refreshStatus = refreshError?.response?.status;
        const refreshCode = refreshError?.response?.data?.code;
        if (refreshStatus === 401 && refreshCode === "SESSION_SUPERSEDED") {
          yield put(
            setAuthStatus({
              authToken: null,
              userProfile: null,
              status: "UNKNOWN",
            })
          );
          yield put(appLoading(true));
          return;
        }
        if (refreshStatus === 401 || refreshError?.message === "LOGGED_OUT") {
          yield* markSignedOut();
          return;
        }
        yield* markStartupError();
        return;
      }
      yield* markSignedOut();
      return;
    }

    yield* markStartupError();
  }
}

export default function* authWatchers() {
  yield all([
    takeLatest(AuthActions.LOGOUT, onLogout),
    takeLatest(AuthActions.CHECK_AUTH_STATUS, checkAuthStatus),
  ]);
}
